/**
 * PortalLanguageControl — Sprach-Umschalter für das Kundenportal.
 *
 * Rendert zwei Dinge:
 *  1. Einen kompakten Umschalter (Globus + Flagge) — z. B. oben auf der
 *     Login-Seite platzierbar.
 *  2. Einen einmaligen Sprach-Dialog beim ersten Öffnen des Portals
 *     (gesteuert über localStorage `portal_lang_chosen`).
 *
 * Die Auswahl setzt i18next + localStorage `mobile_app_language`. Nach dem
 * Login wird sie zusätzlich als `preferred_language` ins Profil geschrieben
 * (siehe CustomerPortal.handleLogin).
 */
import React, { useState, useEffect, useRef } from 'react';
import i18n from '@/i18n';
import { Globe, Check } from 'lucide-react';

export const PORTAL_LANGUAGES = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'sk', name: 'Slovenčina', flag: '🇸🇰' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
];

const norm = (l) => (l || 'de').split('-')[0];

export default function PortalLanguageControl({ firstVisitModal = true }) {
  const [open, setOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [current, setCurrent] = useState(norm(i18n.language));
  const [modalSel, setModalSel] = useState(norm(i18n.language));
  const wrapRef = useRef(null);

  // Aktuelle Sprache mit i18next synchron halten (z. B. wenn nach Login das
  // Profil eine andere preferred_language setzt) → Flagge im Header stimmt.
  useEffect(() => {
    const onChange = (lng) => setCurrent(norm(lng));
    i18n.on('languageChanged', onChange);
    return () => i18n.off('languageChanged', onChange);
  }, []);

  // Einmaliger Dialog beim ersten Besuch
  useEffect(() => {
    if (!firstVisitModal) return;
    try {
      if (!localStorage.getItem('portal_lang_chosen')) {
        setModalSel(norm(i18n.language));
        setShowModal(true);
      }
    } catch (_) { /* ignore */ }
  }, [firstVisitModal]);

  // Dropdown bei Klick ausserhalb schliessen
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const apply = (code) => {
    try {
      localStorage.setItem('mobile_app_language', code);
      localStorage.setItem('portal_lang_chosen', '1');
    } catch (_) { /* ignore */ }
    i18n.changeLanguage(code);
    setCurrent(code);
  };

  const pickFromDropdown = (code) => {
    apply(code);
    setOpen(false);
  };

  const confirmModal = () => {
    apply(modalSel);
    setShowModal(false);
  };

  const currentLang = PORTAL_LANGUAGES.find((l) => l.code === current) || PORTAL_LANGUAGES[0];

  return (
    <>
      {/* Kompakter Umschalter */}
      <div className="relative" ref={wrapRef} data-testid="portal-language-switcher">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 h-9 px-3 rounded-full border border-white/10 bg-white/5 text-white transition-colors"
          aria-label="Sprache wählen / Choose language"
          data-testid="portal-language-trigger"
        >
          <Globe size={16} style={{ color: '#c9a34e' }} />
          <span className="text-sm">{currentLang.flag}</span>
          <span className="text-xs font-semibold uppercase tracking-wide">{currentLang.code}</span>
        </button>

        {open && (
          <div
            className="absolute right-0 mt-2 w-48 rounded-xl overflow-hidden z-50 shadow-2xl"
            style={{
              backgroundColor: '#141310',
              border: '1px solid rgba(255,255,255,0.10)',
            }}
            data-testid="portal-language-menu"
          >
            {PORTAL_LANGUAGES.map((l) => {
              const active = l.code === current;
              return (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => pickFromDropdown(l.code)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-white/5"
                  style={{ color: active ? '#c9a34e' : '#e4e4e7' }}
                  data-testid={`portal-language-item-${l.code}`}
                >
                  <span className="text-base">{l.flag}</span>
                  <span className="text-sm flex-1">{l.name}</span>
                  {active && <Check size={16} style={{ color: '#c9a34e' }} />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Einmaliger Dialog beim ersten Besuch */}
      {showModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
          data-testid="portal-language-modal"
        >
          <div
            className="w-full max-w-sm rounded-2xl p-6"
            style={{ backgroundColor: '#141310', border: '1px solid rgba(255,255,255,0.10)' }}
          >
            <div className="flex flex-col items-center text-center mb-5">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
                style={{ backgroundColor: 'rgba(201,163,78,0.12)', border: '1px solid rgba(201,163,78,0.4)' }}
              >
                <Globe size={26} style={{ color: '#c9a34e' }} />
              </div>
              <h3 className="text-lg font-bold text-white">Sprache · Language</h3>
              <p className="text-xs mt-1" style={{ color: '#a1a1aa' }}>
                Wähle deine Sprache · Choose your language
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-5">
              {PORTAL_LANGUAGES.map((l) => {
                const active = l.code === modalSel;
                return (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => setModalSel(l.code)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-all"
                    style={{
                      backgroundColor: active ? 'rgba(201,163,78,0.12)' : 'rgba(255,255,255,0.04)',
                      borderColor: active ? '#c9a34e' : 'rgba(255,255,255,0.10)',
                      color: active ? '#c9a34e' : '#e4e4e7',
                    }}
                    data-testid={`portal-language-modal-item-${l.code}`}
                  >
                    <span className="text-base">{l.flag}</span>
                    <span className="text-sm font-medium">{l.name}</span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={confirmModal}
              className="w-full h-11 rounded-xl font-semibold active:scale-[0.98] transition-colors"
              style={{ backgroundColor: '#c9a34e', color: '#0a0a0a' }}
              data-testid="portal-language-modal-confirm"
            >
              Weiter · Continue
            </button>
          </div>
        </div>
      )}
    </>
  );
}
